import React, { useEffect, useState } from "react";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import { PrimaryButton, TextField, MessageBar, MessageBarType, Dropdown, Spinner } from "@fluentui/react";
import { Formik } from "formik";
import * as Yup from "yup";
import "./RegisterNewTenantComponent.scss";
import { IAddTenantViewModel } from "../../../models/viewmodels/tenant/IAddTenantViewModel";
import { IOrganizationInfo } from "../../../interfaces/IOrganizationInfo";
import { VerifiedDomain } from "@microsoft/microsoft-graph-types";
import { tenantService } from "../../../services/TenantService";
import { graphService } from "../../../services/GraphService";

interface SetupTenantComponentProps {
  onTenantCreated: () => void;
  intl: IntlShape;
}

const SetupTenantComponent: React.FC<SetupTenantComponentProps> = (props: SetupTenantComponentProps) => {
  const [isFetchingDomains, setIsFetchingDomains] = useState<boolean>(true);
  const [domains, setDomains] = useState<string[]>([]);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const validationSchema = Yup.object().shape({
    organizationName:
      Yup.string()
        .required(
          props.intl.formatMessage({
            id: "general.validation.required",
            defaultMessage: "This field is required.",
          }),
        )
        .min(2,
          props.intl.formatMessage({
              id: "setup.tenant.validation.minLength",
              defaultMessage: "Must be at least {count} characters long.",
            },
            {
              count: 2,
            },
          ),
        )
        .max(100,
          props.intl.formatMessage({
              id: "setup.tenant.validation.maxLength",
              defaultMessage: "Can be at most {count} characters long.",
            },
            {
              count: 100,
            },
          ),
        ),
    contactName:
      Yup.string()
        .required(
          props.intl.formatMessage({
            id: "general.validation.required",
            defaultMessage: "This field is required.",
          }),
        )
        .min(3,
          props.intl.formatMessage({
              id: "setup.tenant.validation.minLength",
              defaultMessage: "Must be at least {count} characters long.",
            },
            {
              count: 3,
            },
          ),
        )
        .max(100,
          props.intl.formatMessage({
              id: "setup.tenant.validation.maxLength",
              defaultMessage: "Can be at most {count} characters long.",
            },
            {
              count: 100,
            },
          ),
        ),
    contactEmail:
      Yup.string()
        .required(
          props.intl.formatMessage({
            id: "general.validation.required",
            defaultMessage: "This field is required.",
          }),
        )
        .email(
          props.intl.formatMessage({
            id: "setup.tenant.validation.email",
            defaultMessage: "Must be a valid email address.",
          }),
        ),
    contactPhone:
      Yup.string()
        .required(
          props.intl.formatMessage({
            id: "general.validation.required",
            defaultMessage: "This field is required.",
          }),
        )
        .matches(/^((\+31|0)([1-9]{1}[0-9]{8}|\d{2}-\d{7}|\d{3}-\d{6}))$/,
          props.intl.formatMessage({
            id: "setup.tenant.validation.phone",
            defaultMessage: "Must be a valid phone number.",
          }),
        ),
    domain:
      Yup.string()
        .required(
          props.intl.formatMessage({
            id: "general.validation.required",
            defaultMessage: "This field is required.",
          }),
        ),
  });

  const [initialValues, setInitialValues] = useState({
    organizationName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    domain: "",
  });

  useEffect(() => {
    graphService.getCurrentUser()
      .then((user) => {
        setInitialValues(prev => ({
          ...prev,
          contactName: user.displayName ?? "",
          contactEmail: user.mail ?? "",
          contactPhone: user.mobilePhone ?? "",
        }));
      })
      .catch(console.error)
      .finally(() => setIsFetchingUser(false));

    graphService.getOrganizationInfo()
      .then((organizationInfo: IOrganizationInfo) => {
        const domainNames = organizationInfo.verifiedDomains
          .filter((domain: VerifiedDomain) => domain?.name != null && domain.name !== "" && domain.name !== undefined)
          .map((domain: VerifiedDomain) => domain.name as string);
        setDomains(domainNames);
        setInitialValues(prev => ({
          ...prev,
          organizationName: organizationInfo.displayName ?? "",
          domain: domainNames[0] ?? "",
        }));
      })
      .catch(() => setError(props.intl.formatMessage({
          id: "setup.tenant.error.fetchDomains",
          defaultMessage: "Could not retrieve verified domains",
        })),
      )
      .finally(() => setIsFetchingDomains(false));
  }, [props.intl]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const newTenant: IAddTenantViewModel = {
      companyName: values.organizationName,
      contactPersonName: values.contactName,
      contactPersonEmailAddress: values.contactEmail,
      contactPersonPhone: values.contactPhone,
      domain: values.domain,
    };

    try {
      await tenantService.setupTenant(newTenant);
      props.onTenantCreated();
    } catch (error) {
      setError(props.intl.formatMessage(
        {
          id: "setup.tenant.error.setupTenant",
          defaultMessage: "Something went wrong creating the tenant. Try again later.",
        },
      ));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="setup-tenant-component">
      <div className="setup-tenant-component-wrapper">
        <div className="form-wrapper">
          <section className="setup-tenant-tile-wrapper">
            <div className="title-and-fields-wrapper">
              <h1 className="tile-header">
                <FormattedMessage id="setup.tenant.header" defaultMessage="Register a new tenant" />
              </h1>
              {(isFetchingDomains || isFetchingUser) && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <Spinner />
                </div>
              )}

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={true}
                onSubmit={handleSubmit}
              >
                {(formikProps) => {
                  const {
                    values,
                    errors,
                    isSubmitting,
                    handleSubmit,
                    setFieldValue,
                    setFieldTouched,
                    validateField,

                  } = formikProps;

                  const handleFieldChange = async (e: React.FormEvent<HTMLElement> | any) => {
                    const { id, value } = e.target;
                    setFieldValue(id, value);
                    setFieldTouched(id, true, false);
                    setTimeout(() => validateField(id), 100);
                  };

                  return (
                    <div className="ilx-form-wrapper" style={{ opacity: isFetchingDomains || isFetchingUser ? 0.5 : 1 }}>
                    <form onSubmit={handleSubmit}>
                        <TextField
                          required
                          minLength={2}
                          maxLength={100}
                          label={
                            props.intl.formatMessage({
                                id: "setup.tenant.fieldName.organizationName",
                                defaultMessage: "Organization name",
                              },
                            )
                          }
                          id="organizationName"
                          errorMessage={errors.organizationName}
                          onChange={handleFieldChange}
                          value={values.organizationName}
                          className="ilx-text-field-small"
                          autoComplete={"off"}
                          disabled={isSubmitting || isFetchingUser}
                        />
                        <TextField
                          required
                          minLength={3}
                          maxLength={100}
                          label={
                            props.intl.formatMessage({
                                id: "setup.tenant.fieldName.contactPersonName",
                                defaultMessage: "Contact person name",
                              },
                            )
                          }
                          id="contactName"
                          errorMessage={errors.contactName}
                          onChange={handleFieldChange}
                          value={values.contactName}
                          className="ilx-text-field-small"
                          autoComplete={"off"}
                          disabled={isSubmitting || isFetchingUser}
                        />
                        <TextField
                          required
                          label={
                            props.intl.formatMessage({
                                id: "setup.tenant.fieldName.contactPersonEmail",
                                defaultMessage: "Contact person email",
                              },
                            )
                          }
                          id="contactEmail"
                          type="email"
                          errorMessage={errors.contactEmail}
                          onChange={handleFieldChange}
                          value={values.contactEmail}
                          className="ilx-text-field-small"
                          autoComplete={"off"}
                          disabled={isSubmitting || isFetchingUser}
                        />
                        <TextField
                          required
                          label={
                            props.intl.formatMessage({
                                id: "setup.tenant.fieldName.contactPersonPhone",
                                defaultMessage: "Contact person phone number",
                              },
                            )
                          }
                          id="contactPhone"
                          placeholder={
                            props.intl.formatMessage({
                                id: "setup.tenant.fieldName.phonePlaceholder",
                                defaultMessage: "+31612345678 or 0612345678",
                              },
                            )
                          }
                          errorMessage={errors.contactPhone}
                          onChange={handleFieldChange}
                          value={values.contactPhone}
                          className="ilx-text-field-small"
                          autoComplete={"off"}
                          disabled={isSubmitting || isFetchingUser}
                        />
                        <Dropdown
                          required
                          label={
                            props.intl.formatMessage({
                              id: "setup.tenant.fieldName.domain",
                              defaultMessage: "Domain",
                            })
                          }
                          id="domain"
                          options={domains.filter(domain => domain != null && domain !== "").map(domain => ({
                            key: domain,
                            text: domain,
                          }))}
                          selectedKey={values.domain}
                          onChange={(e, option) => {
                            setFieldValue("domain", option?.key || "");
                            setFieldTouched("domain", true, false);
                            setTimeout(() => validateField("domain"), 100);
                          }}
                          errorMessage={errors.domain}
                          disabled={isSubmitting || isFetchingDomains}
                        />
                        {error && (
                          <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            dismissButtonAriaLabel="Close"
                          >
                            {error}
                          </MessageBar>
                        )}

                        <PrimaryButton type="submit" disabled={isSubmitting}>
                          <FormattedMessage id="setup.tenant.requestTrial" defaultMessage="Request trial license" />
                        </PrimaryButton>
                      </form>
                    </div>
                  );
                }}
              </Formik>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(SetupTenantComponent);
